import type { PluginListenerHandle } from "@capacitor/core";
import { useEffect, useRef, useState, type MutableRefObject } from "react";
import { useTranslation } from "react-i18next";
import type { LiveRecordingStatus } from "@/recording/recording";
import {
  getRecordingAuthToken,
  getRecordingUiState,
  recordRecordingStatus,
  notifyRecordingNow,
  onRecordingAppStateChange,
  useRecordingAuthToken,
  useRecordingUiSelector,
} from "./ports";
import { attachNativeRecordingListeners } from "./lifecycle";
import {
  clearLiveRecordingRecoveryManifest,
  getLiveRecordingRecoveryForPlatform,
  getLiveRecordingRecoveryForTranscript,
  setLiveRecordingRecoveryManifest,
  touchLiveRecordingRecoveryManifest,
  type LiveRecordingRecoveryPlatform,
} from "./recovery";

export type { LiveRecordingStatus } from "@/recording/recording";

let _serverUrl: string | undefined;

export const configureLiveRecorderServerUrl = (serverUrl: string): void => {
  _serverUrl = serverUrl;
};

const _getServerUrl = (): string => {
  if (!_serverUrl) {
    throw new Error("Live recorder URL used before controller construction");
  }
  return _serverUrl;
};

export type RecordingHealthStatus = "healthy" | "warning" | "error";

export interface RecordingHealth {
  status: RecordingHealthStatus;
  pendingChunks: number;
  pendingBytes?: number;
  hasNetworkError: boolean;
  isNetworkAvailable: boolean;
  lastChunkSentAt: number | null;
  isServerReady?: boolean;
  isLocalBackupReady?: boolean;
  hasDataLoss?: boolean;
  droppedChunks?: number;
  failedChunks?: number;
  lastErrorMessage?: string | null;
  isSilenced?: boolean;
  pausedForSilence?: boolean;
  isBatteryOptimizationIgnored?: boolean;
}

export interface StreamStatus {
  finalSequenceNumber?: number;
  status: LiveRecordingStatus;
  message?: string;
  sampleRate?: number;
  channels?: number;
  bitsPerSample?: number;
  networkStatus?: "error" | "ok";
  audioLevel?: number;
  healthStatus?: RecordingHealthStatus;
  pendingChunks?: number;
  pendingBytes?: number;
  hasNetworkError?: boolean;
  isNetworkAvailable?: boolean;
  lastChunkSentAt?: number;
  isServerReady?: boolean;
  isLocalBackupReady?: boolean;
  hasDataLoss?: boolean;
  droppedChunks?: number;
  failedChunks?: number;
  lastErrorMessage?: string;
  isSilenced?: boolean;
  pausedForSilence?: boolean;
  notificationPermissionGranted?: boolean;
  silenceAlertPosted?: boolean;
  isBatteryOptimizationIgnored?: boolean;
}

export interface EngineState {
  isEngineRunning: boolean;
  isPaused: boolean;
  stopRequested?: boolean;
  lastAudioAppendedAt?: number;
  isSilenced?: boolean;
  pausedForSilence?: boolean;
  isBatteryOptimizationIgnored?: boolean;
}

export interface StopStreamResult {
  success?: boolean;
  isAwaitingUpload?: boolean;
  pendingChunks?: number;
  pendingBytes?: number;
  isServerReady?: boolean;
  isLocalBackupReady?: boolean;
  hasDataLoss?: boolean;
  droppedChunks?: number;
  failedChunks?: number;
  message?: string;
}

export interface StopRecordingError {
  message?: string;
  pendingChunks?: number;
  pendingBytes?: number;
  isServerReady?: boolean;
  isLocalBackupReady?: boolean;
  hasDataLoss?: boolean;
  droppedChunks?: number;
  failedChunks?: number;
}

export interface RecordingRecoveryStatus {
  finalSequenceNumber?: number;
  transcriptId: string;
  startedAt: number;
  pendingChunks?: number;
  pendingBytes?: number;
  uploadedBytes?: number;
  recoveryMode?: "pending" | "blocked";
  lastErrorMessage?: string | null;
}

export interface AudioLevelEvent {
  audioLevel: number;
}

export type LiveRecorderHook = {
  status: LiveRecordingStatus;
  elapsedTime: number;
  audioLevel?: number;
  recordingHealth?: RecordingHealth;
  startRecording: () => Promise<boolean>;
  stopRecording: (params: {
    onSuccess: () => Promise<void> | void;
    onError: (error?: StopRecordingError) => Promise<void> | void;
  }) => Promise<void>;
  pauseOrPlay: () => Promise<void>;
  cancelRecording: (
    callback: (result: { success: boolean; error?: unknown }) => void,
  ) => Promise<void>;
  getRecoveryStatus: (excludeTranscriptIds?: readonly string[]) => Promise<RecordingRecoveryStatus | null>;
  resumeRecoveryUpload: (params: {
    transcriptId: string;
    retryBlocked?: boolean;
  }) => Promise<void>;
  discardRecovery: (params: { transcriptId: string }) => Promise<void>;
  getQueueStatus?: () => any;
};

type NativeLiveRecordingPlatform = Extract<
  LiveRecordingRecoveryPlatform,
  "android" | "ios"
>;

export interface NativeLiveRecordingPlugin {
  startStream(options?: {
    chunkIntervalSeconds?: number;
    transcriptId?: string;
    authToken: string;
    serverUrl: string;
    silenceAlertTitle?: string;
    silenceAlertBody?: string;
    silencePausedBody?: string;
  }): Promise<void>;
  stopStream(): Promise<StopStreamResult>;
  getRecoveryStatus(): Promise<Partial<RecordingRecoveryStatus>>;
  resumeRecoveryUpload(options: {
    transcriptId: string;
    authToken: string;
    serverUrl: string;
    retryBlocked?: boolean;
  }): Promise<StopStreamResult>;
  discardRecovery(options: { transcriptId: string }): Promise<void>;
  pauseStream(): Promise<void>;
  resumeStream(): Promise<void>;
  cancelStream(): Promise<void>;
  // Native introspection used to reconcile UI truth on app resume
  // (implemented on both Android and iOS).
  getEngineState?(): Promise<EngineState>;
  // Pushes a refreshed auth token into an active native recording session so
  // multi-hour recordings keep uploading after the original token expires.
  updateAuthToken?(options: { authToken: string }): Promise<void>;
  addListener(
    eventName: string,
    listenerFunc: (...args: any[]) => void,
  ): Promise<PluginListenerHandle> & PluginListenerHandle;
}

type NativeStopCompletion = {
  resolve: () => void;
  reject: (error: StopRecordingError) => void;
};

type NativeExtraListenersParams<TPlugin extends NativeLiveRecordingPlugin> = {
  addListenerHandle: (handle: PluginListenerHandle) => void;
  appIsActiveRef: MutableRefObject<boolean>;
  durationRef: MutableRefObject<number>;
  plugin: TPlugin;
  startTimeRef: MutableRefObject<number | null>;
  t: (key: string) => string;
};

export type LiveRecorderStatusHelpers = {
  // Surfaces a JS-side warning (e.g. foreground-service start failure) on
  // recordingHealth without being clobbered by the next native health payload.
  // Pass null to clear the warning.
  setHealthWarning: (message: string | null) => void;
};

type UseNativeLiveRecorderOptions<TPlugin extends NativeLiveRecordingPlugin> = {
  addExtraListeners?: (
    params: NativeExtraListenersParams<TPlugin>,
  ) => Promise<void> | void;
  audioLevelThrottleMs?: number;
  canCancelWhileUploading?: boolean;
  onAppActive?: (params: {
    status: LiveRecordingStatus;
    helpers: LiveRecorderStatusHelpers;
  }) => Promise<void> | void;
  onBeforeStart?: () => Promise<void> | void;
  onCleanup?: () => Promise<void> | void;
  onStatusChange?: (
    info: StreamStatus,
    helpers: LiveRecorderStatusHelpers,
  ) => Promise<void> | void;
  pauseElapsedWhileInactive?: boolean;
  platform: NativeLiveRecordingPlatform;
  plugin: TPlugin;
  reliabilityNotificationId: number;
};

type RecoverInterruptedLiveRecordingParams = {
  excludeTranscriptIds?: readonly string[];
  retryBlocked?: boolean;
  onDiscardEmpty: (params: {
    recoveryStatus: RecordingRecoveryStatus;
    transcriptId: string;
  }) => Promise<void> | void;
  onRecovered: (params: {
    recoveryStatus: RecordingRecoveryStatus;
    transcriptId: string;
  }) => Promise<void> | void;
  onRecoveryStarted?: (params: {
    recoveryStatus: RecordingRecoveryStatus;
    transcriptId: string;
  }) => Promise<void> | void;
  onRecoveryBlocked?: (params: {
    recoveryStatus: RecordingRecoveryStatus;
    transcriptId: string;
  }) => Promise<void> | void;
  onRecoveryFailed: (params: {
    recoveryStatus: RecordingRecoveryStatus;
    transcriptId: string;
    error: unknown;
  }) => Promise<void> | void;
  recorder: Pick<
    LiveRecorderHook,
    "discardRecovery" | "getRecoveryStatus" | "resumeRecoveryUpload"
  >;
};

type RecoverInterruptedLiveRecordingResult = {
  recovered: boolean;
  blocked?: boolean;
  transcriptId?: string;
  recoveryStatus?: RecordingRecoveryStatus;
};

const RESET_ELAPSED_STATUSES: LiveRecordingStatus[] = [
  "inactive",
  "starting",
  "cancelled",
  "error",
  "permissionDenied",
];

const TERMINAL_LISTENER_STATUSES: LiveRecordingStatus[] = [
  "cancelled",
  "error",
  "permissionDenied",
];

const ACTIVE_RECORDING_STATUSES: LiveRecordingStatus[] = [
  "starting",
  "recording",
  "paused",
  "uploading",
  // "warning" means the session is live but the mic is silenced/unhealthy —
  // still an active session that must not be double-started.
  "warning",
];

// Cap how long stopRecording waits for the upload pipeline to drain. If we're
// still waiting after this, we abandon the wait and surface a save-failed error
// so the user can retry via the recovery flow on the next launch instead of
// staring at an indefinite spinner. 150s (not 90s) so it comfortably covers a
// first chunk attempt (45s) plus one escalated retry (90s) — otherwise a
// legitimately in-flight retry outlives the timer and the user sees a spurious
// failure for a save that then completes in the background.
const STOP_UPLOAD_DRAIN_TIMEOUT_MS = 150 * 1000;

export const createDefaultRecordingHealth = (): RecordingHealth => ({
  status: "healthy",
  pendingChunks: 0,
  hasNetworkError: false,
  isNetworkAvailable: true,
  lastChunkSentAt: null,
  isServerReady: true,
  isLocalBackupReady: true,
  hasDataLoss: false,
  droppedChunks: 0,
  failedChunks: 0,
  lastErrorMessage: null,
});

export const buildStopRecordingError = (
  source?: Partial<StreamStatus> & Partial<StopStreamResult>,
): StopRecordingError => ({
  message:
    source?.message ??
    source?.lastErrorMessage ??
    "The recording could not be saved reliably.",
  pendingChunks: source?.pendingChunks,
  pendingBytes: source?.pendingBytes,
  isServerReady: source?.isServerReady,
  isLocalBackupReady: source?.isLocalBackupReady,
  hasDataLoss: source?.hasDataLoss,
  droppedChunks: source?.droppedChunks,
  failedChunks: source?.failedChunks,
});

const buildRecordingHealth = (source: StreamStatus): RecordingHealth => ({
  status: source.healthStatus ?? "healthy",
  pendingChunks: source.pendingChunks ?? 0,
  pendingBytes: source.pendingBytes,
  hasNetworkError: source.hasNetworkError ?? false,
  isNetworkAvailable: source.isNetworkAvailable ?? true,
  lastChunkSentAt: source.lastChunkSentAt ?? null,
  isServerReady: source.isServerReady ?? true,
  isLocalBackupReady: source.isLocalBackupReady ?? true,
  hasDataLoss: source.hasDataLoss ?? false,
  droppedChunks: source.droppedChunks ?? 0,
  failedChunks: source.failedChunks ?? 0,
  lastErrorMessage: source.lastErrorMessage ?? null,
  isSilenced: source.isSilenced ?? false,
  pausedForSilence: source.pausedForSilence ?? false,
  isBatteryOptimizationIgnored: source.isBatteryOptimizationIgnored,
});

const isDefinitelyEmptyRecovery = (recoveryStatus: RecordingRecoveryStatus) =>
  recoveryStatus.pendingBytes !== undefined &&
  recoveryStatus.uploadedBytes !== undefined &&
  recoveryStatus.pendingBytes <= 0 &&
  recoveryStatus.uploadedBytes <= 0;

export const recoverInterruptedLiveRecording = async ({
  retryBlocked,
  excludeTranscriptIds,
  onDiscardEmpty,
  onRecoveryBlocked,
  onRecovered,
  onRecoveryFailed,
  onRecoveryStarted,
  recorder,
}: RecoverInterruptedLiveRecordingParams): Promise<RecoverInterruptedLiveRecordingResult> => {
  const ui = getRecordingUiState();
  if ((ui.liveRecording && ui.liveRecording.status !== "error") || ui.desktopCaptureActive) return { recovered: false };
  const recoveryStatus = await recorder.getRecoveryStatus(excludeTranscriptIds);
  if (!recoveryStatus?.transcriptId) {
    return { recovered: false, transcriptId: undefined };
  }

  const transcriptId = recoveryStatus.transcriptId;

  if (isDefinitelyEmptyRecovery(recoveryStatus)) {
    await recorder.discardRecovery({ transcriptId });
    await onDiscardEmpty({ recoveryStatus, transcriptId });
    return { recovered: true, transcriptId };
  }

  if (recoveryStatus.recoveryMode === "blocked" && !retryBlocked) {
    await onRecoveryBlocked?.({ recoveryStatus, transcriptId });
    return {
      recovered: false,
      blocked: true,
      transcriptId,
      recoveryStatus,
    };
  }

  await onRecoveryStarted?.({ recoveryStatus, transcriptId });

  getRecordingUiState().setLiveRecording({
    itemId: transcriptId,
    status: "uploading",
  });

  try {
    await recorder.resumeRecoveryUpload({ transcriptId, retryBlocked });
    // resumeRecoveryUpload deliberately keeps the recovery manifest so a finalize
    // failure can be retried. We discard it ONLY after onRecovered (which performs
    // the server finalize) returns successfully.
    await onRecovered({ recoveryStatus, transcriptId });
    await recorder.discardRecovery({ transcriptId });
    return { recovered: true, transcriptId };
  } catch (error) {
    // Surface the failure through the required UI boundary. Return blocked so
    // bootstrap treats recovery as handled and does not start a fresh recording
    // on top of the failure modal.
    await onRecoveryFailed({ recoveryStatus, transcriptId, error });
    return { recovered: false, blocked: true, transcriptId, recoveryStatus };
  } finally {
    if (getRecordingUiState().liveRecording?.itemId === transcriptId) {
      getRecordingUiState().setLiveRecording(undefined);
    }
  }
};

export const useNativeLiveRecorder = <
  TPlugin extends NativeLiveRecordingPlugin,
>({
  addExtraListeners,
  audioLevelThrottleMs,
  canCancelWhileUploading = false,
  onAppActive,
  onBeforeStart,
  onCleanup,
  onStatusChange,
  pauseElapsedWhileInactive = false,
  platform,
  plugin,
  reliabilityNotificationId,
}: UseNativeLiveRecorderOptions<TPlugin>): LiveRecorderHook => {
  const status = useRecordingUiSelector(
    (state) => state.liveRecording?.status || "inactive",
  );
  const listenerHandlesRef = useRef<PluginListenerHandle[]>([]);
  const startPromiseRef = useRef<Promise<boolean> | null>(null);
  const cancelPendingStartRef = useRef(false);
  const stopCompletionRef = useRef<NativeStopCompletion | null>(null);
  const stopPromiseRef = useRef<Promise<void> | null>(null);
  const appIsActiveRef = useRef(true);
  const lastBackgroundNotificationKeyRef = useRef<string | null>(null);
  const lastAudioLevelCommitRef = useRef(0);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [audioLevel, setAudioLevel] = useState(0);
  const [recordingHealth, setRecordingHealth] = useState<RecordingHealth>(() =>
    createDefaultRecordingHealth(),
  );
  const startTimeRef = useRef<number | null>(null);
  const durationRef = useRef<number>(0);
  const timerIntervalRef = useRef<NodeJS.Timeout | undefined>(undefined);
  const externalHealthWarningRef = useRef<string | null>(null);
  const { t } = useTranslation();

  const setHealthWarning = (message: string | null) => {
    externalHealthWarningRef.current = message;
    if (message) {
      setRecordingHealth((prev) => ({
        ...prev,
        status: prev.status === "error" ? "error" : "warning",
        lastErrorMessage: prev.lastErrorMessage ?? message,
      }));
    }
  };

  const statusHelpers: LiveRecorderStatusHelpers = { setHealthWarning };

  // On app resume, ask the native engine whether it is actually capturing. If
  // the UI claims an active session but the engine is dead (activity destroyed,
  // process partially torn down, mic revoked), flip to error immediately so the
  // recovery flow takes over — never keep showing a fake timer.
  const reconcileEngineStateOnResume = async () => {
    if (!plugin.getEngineState) {
      return;
    }

    const activeStatuses: LiveRecordingStatus[] = [
      "recording",
      "paused",
      "warning",
    ];
    if (
      !activeStatuses.includes(
        getRecordingUiState().liveRecording?.status as LiveRecordingStatus,
      )
    ) {
      return;
    }

    try {
      const engineState = await plugin.getEngineState();
      const latestStatus = getRecordingUiState().liveRecording?.status;
      if (
        !latestStatus ||
        !activeStatuses.includes(latestStatus) ||
        engineState?.isEngineRunning
      ) {
        return;
      }

      console.error(
        `useNativeLiveRecorder(${platform}): engine is not running while UI says "${latestStatus}" - reconciling to error`,
      );
      updateCurrentLiveRecordingStatus("error");
      setRecordingHealth((prev) => ({
        ...prev,
        status: "error",
        lastErrorMessage: t("useLiveRecorder.recordingInterruptedMessage"),
      }));
    } catch (error) {
      console.error(
        `Failed to reconcile ${platform} engine state on resume:`,
        error,
      );
    }
  };

  // A recording session can outlive the auth token it started with. Whenever
  // the token refreshes during an active session, push it into the native
  // plugin so uploads don't stall on 401s with a stale token for hours.
  const userToken = useRecordingAuthToken();
  useEffect(() => {
    if (!userToken) {
      return;
    }

    const currentStatus = getRecordingUiState().liveRecording?.status;
    if (
      !currentStatus ||
      !ACTIVE_RECORDING_STATUSES.includes(currentStatus as LiveRecordingStatus)
    ) {
      return;
    }

    void plugin.updateAuthToken?.({ authToken: userToken }).catch((error) => {
      // Older native binaries without this method reject — harmless; they
      // keep the session-start token exactly as before.
      console.error(
        `Failed to push refreshed auth token to ${platform} recorder:`,
        error,
      );
    });
    // plugin/platform are stable for the lifetime of the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken]);

  useEffect(() => {
    const listenerPromise = onRecordingAppStateChange(({ isActive }) => {
      appIsActiveRef.current = isActive;

      if (isActive) {
        lastBackgroundNotificationKeyRef.current = null;

        void reconcileEngineStateOnResume();
        void Promise.resolve(
          onAppActive?.({
            status: getRecordingUiState().liveRecording?.status ?? "inactive",
            helpers: statusHelpers,
          }),
        ).catch((error) => {
          console.error(`Failed to handle ${platform} app-active hook:`, error);
        });

        if (
          pauseElapsedWhileInactive &&
          status === "recording" &&
          startTimeRef.current === null
        ) {
          startTimeRef.current = Date.now();
        }
        return;
      }

      if (
        pauseElapsedWhileInactive &&
        status === "recording" &&
        startTimeRef.current
      ) {
        durationRef.current += Date.now() - startTimeRef.current;
        startTimeRef.current = null;
      }
    });

    return () => {
      listenerPromise.then((listener) => listener.remove());
    };
  }, [pauseElapsedWhileInactive, status]);

  const commitAudioLevel = (level: number) => {
    if (!audioLevelThrottleMs) {
      setAudioLevel(level);
      return;
    }

    const now = Date.now();
    if (now - lastAudioLevelCommitRef.current >= audioLevelThrottleMs) {
      lastAudioLevelCommitRef.current = now;
      setAudioLevel(level);
    }
  };

  const maybeNotifyInBackground = async (info: StreamStatus) => {
    if (appIsActiveRef.current || !info.message) {
      return;
    }

    if (
      info.status === "stopped" ||
      info.message === "All chunks sent successfully"
    ) {
      return;
    }

    const notificationKey = `${info.status}:${info.message}`;
    if (lastBackgroundNotificationKeyRef.current === notificationKey) {
      return;
    }

    lastBackgroundNotificationKeyRef.current = notificationKey;

    try {
      await notifyRecordingNow({
        id: reliabilityNotificationId,
        title:
          info.status === "error"
            ? "Recording needs attention"
            : "Recording upload update",
        body: info.message,
      });
    } catch (error) {
      console.error("Failed to send background recording notification:", error);
    }
  };

  const removeListeners = async () => {
    const handles = listenerHandlesRef.current;
    listenerHandlesRef.current = [];

    await Promise.allSettled(handles.map((handle) => handle.remove()));
  };

  const updateCurrentLiveRecordingStatus = (
    nextStatus: LiveRecordingStatus,
  ) => {
    const currentLiveRecording = getRecordingUiState().liveRecording;
    if (!currentLiveRecording?.itemId) {
      return false;
    }

    getRecordingUiState().setLiveRecording({
      ...currentLiveRecording,
      status: nextStatus,
    });

    return true;
  };

  const cleanup = async () => {
    if (timerIntervalRef.current) {
      clearInterval(timerIntervalRef.current);
      timerIntervalRef.current = undefined;
    }

    // Reject any pending stop completion so awaiters don't hang forever.
    if (stopCompletionRef.current) {
      stopCompletionRef.current.reject(
        buildStopRecordingError({
          message:
            "The recording session was cleaned up before stopping completed.",
        }),
      );
      stopCompletionRef.current = null;
    }
    stopPromiseRef.current = null;

    startTimeRef.current = null;
    durationRef.current = 0;
    lastAudioLevelCommitRef.current = 0;
    lastBackgroundNotificationKeyRef.current = null;
    externalHealthWarningRef.current = null;
    setElapsedTime(0);
    setAudioLevel(0);
    setRecordingHealth(createDefaultRecordingHealth());

    await removeListeners();
    await onCleanup?.();
  };

  useEffect(() => {
    if (status === "recording" || status === "warning") {
      timerIntervalRef.current = setInterval(() => {
        if (startTimeRef.current) {
          setElapsedTime(
            Date.now() - startTimeRef.current + durationRef.current,
          );
        }
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = undefined;
      }

      if (RESET_ELAPSED_STATUSES.includes(status)) {
        setElapsedTime(0);
      }
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
        timerIntervalRef.current = undefined;
      }
    };
  }, [status]);

  const handleStatusChange = async (info: StreamStatus) => {
    const currentLiveRecording = getRecordingUiState().liveRecording;

    // If liveRecording was cleared (e.g. user cancelled during startup),
    // ignore late status events from the native plugin. Without this guard
    // the spread below would create { status: "recording" } with no itemId,
    // producing a ghost recording with no UI and no cleanup path.
    if (!currentLiveRecording?.itemId) {
      return;
    }

    const previousStatus = currentLiveRecording.status;
    if (
      info.status === "paused" &&
      previousStatus !== "paused" &&
      startTimeRef.current
    ) {
      durationRef.current += Date.now() - startTimeRef.current;
      startTimeRef.current = null;
    } else if (
      (info.status === "recording" || info.status === "warning") &&
      previousStatus === "paused" &&
      startTimeRef.current === null
    ) {
      startTimeRef.current = Date.now();
    }

    getRecordingUiState().setLiveRecording({
      ...currentLiveRecording,
      status: info.status,
    });
    recordRecordingStatus({
      status: info.status,
      healthStatus: info.healthStatus,
      pendingChunks: info.pendingChunks,
      pendingBytes: info.pendingBytes,
      isLocalBackupReady: info.isLocalBackupReady,
      hasDataLoss: info.hasDataLoss,
      droppedChunks: info.droppedChunks,
      failedChunks: info.failedChunks,
      isSilenced: info.isSilenced,
      pausedForSilence: info.pausedForSilence,
      notificationPermissionGranted: info.notificationPermissionGranted,
      silenceAlertPosted: info.silenceAlertPosted,
    });

    if (info.healthStatus !== undefined) {
      const health = buildRecordingHealth(info);
      // Keep JS-side warnings (e.g. foreground-service failure) visible even
      // when the native payload reports healthy.
      if (externalHealthWarningRef.current && health.status === "healthy") {
        health.status = "warning";
        health.lastErrorMessage =
          health.lastErrorMessage ?? externalHealthWarningRef.current;
      }
      setRecordingHealth(health);
    }

    if (typeof info.audioLevel === "number") {
      commitAudioLevel(info.audioLevel);
    }

    if (info.message) {
      await maybeNotifyInBackground(info);
    }

    if (onStatusChange) {
      try {
        await onStatusChange(info, statusHelpers);
      } catch (error) {
        console.error(
          `Failed to handle ${platform} stream status update:`,
          error,
        );
      }
    }

    if (info.status === "recording") {
      const transcriptId = getRecordingUiState().liveRecording?.itemId;
      if (transcriptId) {
        await touchLiveRecordingRecoveryManifest(transcriptId, {
          status: "recording",
          lastErrorMessage: null,
        });
      }
    }

    if (info.status === "uploading") {
      const transcriptId = getRecordingUiState().liveRecording?.itemId;
      if (transcriptId) {
        await touchLiveRecordingRecoveryManifest(transcriptId, {
          status: "uploading",
          lastErrorMessage: null,
        });
      }
    }

    if (info.status === "stopped" && info.finalSequenceNumber !== undefined) {
      const transcriptId = currentLiveRecording?.itemId;
      if (transcriptId) {
        await touchLiveRecordingRecoveryManifest(transcriptId, { status: "uploading", finalSequenceNumber: info.finalSequenceNumber });
      }
    }

    if (info.status === "stopped" && stopCompletionRef.current) {
      stopCompletionRef.current.resolve();
      stopCompletionRef.current = null;
    }

    if (info.status === "error" && stopCompletionRef.current) {
      stopCompletionRef.current.reject(buildStopRecordingError(info));
      stopCompletionRef.current = null;
    }

    if (TERMINAL_LISTENER_STATUSES.includes(info.status)) {
      if (["cancelled", "permissionDenied"].includes(info.status)) {
        const itemId = getRecordingUiState().liveRecording?.itemId;
        await clearLiveRecordingRecoveryManifest(itemId);
      }
      await removeListeners();
    }
  };

  const addListeners = async () => {
    await removeListeners();

    try {
      const addListenerHandle = (handle: PluginListenerHandle) => {
        listenerHandlesRef.current.push(handle);
      };

      const coreListeners = await attachNativeRecordingListeners(plugin, {
        onStatus: handleStatusChange,
        onAudioLevel: (event: AudioLevelEvent) => {
          commitAudioLevel(event.audioLevel);
        },
      });
      coreListeners.forEach(addListenerHandle);

      await addExtraListeners?.({
        addListenerHandle,
        appIsActiveRef,
        durationRef,
        plugin,
        startTimeRef,
        t,
      });
    } catch (error) {
      console.error(`Failed to add ${platform} recording listeners:`, error);
      await removeListeners();
      // Propagate the failure so startRecording aborts. Without listeners,
      // the recorder cannot deliver status updates or stop completion events,
      // so a half-initialised native recorder would silently misbehave.
      throw error;
    }
  };

  const waitForStopCompletion = () => {
    // If a stop is already in flight, return the SAME promise so duplicate
    // stop requests resolve/reject together. This makes stop idempotent and
    // avoids the false "stop superseded" path on rapid double-clicks.
    if (stopPromiseRef.current) {
      return stopPromiseRef.current;
    }

    const promise = new Promise<void>((resolve, reject) => {
      stopCompletionRef.current = {
        resolve: () => {
          stopPromiseRef.current = null;
          resolve();
        },
        reject: (error) => {
          stopPromiseRef.current = null;
          reject(error);
        },
      };
    });
    stopPromiseRef.current = promise;
    return promise;
  };

  // Waits for the upload pipeline to drain. Does NOT clear the recovery manifest
  // or cleanup recorder state - those happen in stopRecording AFTER the caller's
  // onSuccess (server finalize) has succeeded, so a finalize failure can be
  // retried from the still-intact local backup.
  const completePendingUpload = async (params: {
    command: () => Promise<StopStreamResult>;
  }) => {
    const waitForCompletion = waitForStopCompletion();
    const commandResult = await params.command();
    const hasPendingWork =
      (commandResult?.pendingChunks ?? 0) > 0 ||
      (commandResult?.pendingBytes ?? 0) > 0;

    if (
      commandResult?.success &&
      !commandResult?.isAwaitingUpload &&
      !hasPendingWork
    ) {
      // Nothing left to drain — resolve the stop-completion promise we just
      // created so it doesn't sit orphaned until cleanup() rejects it as an
      // unhandled promise rejection.
      if (stopCompletionRef.current) {
        stopCompletionRef.current.resolve();
        stopCompletionRef.current = null;
      }
      stopPromiseRef.current = null;
      return;
    }

    if (commandResult?.hasDataLoss) {
      throw buildStopRecordingError(commandResult);
    }

    // Race the upload-drain wait against a hard timeout. If the pipeline is
    // stuck (offline, auth-loss, transient server error), we don't want the
    // user staring at an indefinite spinner - the local backup remains and
    // recovery will pick it up on next launch.
    let timeoutId: ReturnType<typeof setTimeout> | undefined;
    const timeoutError = buildStopRecordingError({
      message:
        "Upload is taking too long. The recording is backed up on this device and will retry the next time you open the app.",
      pendingChunks: commandResult?.pendingChunks,
      pendingBytes: commandResult?.pendingBytes,
      isLocalBackupReady: commandResult?.isLocalBackupReady,
    });
    const timeoutPromise = new Promise<never>((_, reject) => {
      timeoutId = setTimeout(
        () => reject(timeoutError),
        STOP_UPLOAD_DRAIN_TIMEOUT_MS,
      );
    });

    try {
      await Promise.race([waitForCompletion, timeoutPromise]);
    } finally {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    }
  };

  const startRecording = async () => {
    if (ACTIVE_RECORDING_STATUSES.includes(status) || startPromiseRef.current) {
      return false;
    }

    const startPromise = (async () => {
      const transcriptId = getRecordingUiState().liveRecording?.itemId;

      try {
        const authToken = getRecordingAuthToken();
        const serverUrl = _getServerUrl();

        if (!transcriptId || !authToken || !serverUrl) {
          console.error(
            `useNativeLiveRecorder(${platform}): missing parameters`,
            {
              authToken: !!authToken,
              serverUrl: !!serverUrl,
              transcriptId: !!transcriptId,
            },
          );
          updateCurrentLiveRecordingStatus("error");
          return false;
        }

        cancelPendingStartRef.current = false;
        updateCurrentLiveRecordingStatus("starting");

        await onBeforeStart?.();
        await addListeners();
        startTimeRef.current = Date.now();
        durationRef.current = 0;
        setElapsedTime(0);

        await plugin.startStream({
          chunkIntervalSeconds: 15,
          transcriptId,
          authToken,
          serverUrl,
          silenceAlertTitle: t("useLiveRecorder.recordingNeedsAttention"),
          silenceAlertBody: t(
            "useLiveRecorder.backgroundRecordingUnavailable",
          ),
          silencePausedBody: t("useLiveRecorder.interruptionEndedBody"),
        });

        // If the user cancelled/dismissed while startStream was in flight, or
        // the UI was torn down before the native status event reached JS,
        // cancel the native recorder immediately to prevent a ghost session.
        if (
          cancelPendingStartRef.current ||
          !getRecordingUiState().liveRecording?.itemId
        ) {
          try {
            await plugin.cancelStream();
          } catch {
            // best-effort
          }
          await clearLiveRecordingRecoveryManifest(transcriptId);
          await cleanup();
          return false;
        }

        const manifestPersisted = await setLiveRecordingRecoveryManifest({
          platform,
          transcriptId,
          status: "recording",
          startedAt: startTimeRef.current ?? Date.now(),
          lastErrorMessage: null,
        });

        if (!manifestPersisted) {
          // The native plugin keeps its own backup, but the JS-side manifest
          // is what the recovery flow looks at on app restart. Surface a warning
          // so the user knows that crash recovery may not work for this session.
          setRecordingHealth((prev) => ({
            ...prev,
            status: "warning",
            lastErrorMessage:
              "Crash recovery may be unavailable. The session is still being recorded and backed up locally.",
          }));
        }

        return true;
      } catch (error) {
        console.error(`Failed to start ${platform} recording:`, error);
        await clearLiveRecordingRecoveryManifest(
          transcriptId ?? getRecordingUiState().liveRecording?.itemId,
        );
        if (
          getRecordingUiState().liveRecording?.status !== "permissionDenied"
        ) {
          updateCurrentLiveRecordingStatus("error");
        }
        await cleanup();
        return false;
      } finally {
        cancelPendingStartRef.current = false;
      }
    })();

    startPromiseRef.current = startPromise;

    try {
      return await startPromise;
    } finally {
      if (startPromiseRef.current === startPromise) {
        startPromiseRef.current = null;
      }
    }
  };

  const stopRecording = async (params: {
    onSuccess: () => Promise<void> | void;
    onError: (error?: StopRecordingError) => Promise<void> | void;
  }) => {
    if (status === "error") {
      await params.onError(
        buildStopRecordingError({
          message:
            recordingHealth?.lastErrorMessage ||
            "This recording could not be finalized automatically. Audio remains backed up on this device.",
          pendingChunks: recordingHealth?.pendingChunks,
          pendingBytes: recordingHealth?.pendingBytes,
          isLocalBackupReady: recordingHealth?.isLocalBackupReady,
          hasDataLoss: recordingHealth?.hasDataLoss,
          droppedChunks: recordingHealth?.droppedChunks,
          failedChunks: recordingHealth?.failedChunks,
        }),
      );
      return;
    }

    const transcriptId = getRecordingUiState().liveRecording?.itemId;

    try {
      if (transcriptId) await touchLiveRecordingRecoveryManifest(transcriptId, { status: "uploading" });
      await completePendingUpload({
        command: () => plugin.stopStream(),
      });
      // Upload complete. Call onSuccess (which performs the server finalize) BEFORE
      // clearing the local backup so a finalize failure can be retried.
      await params.onSuccess();
      // Finalize confirmed — NOW it is safe to delete the native backup and
      // recovery state (the native side preserves them across "stopped"
      // precisely so a failed finalize stays retryable on the next launch).
      if (transcriptId) {
        try {
          await plugin.discardRecovery({ transcriptId });
        } catch (error) {
          // Non-fatal: the stale-backup purge will clean the file up, and a
          // leftover recovery state re-runs the (idempotent) finalize once.
          console.error(
            `Failed to discard ${platform} recovery after finalize:`,
            error,
          );
        }
      }
      await clearLiveRecordingRecoveryManifest(transcriptId);
      await cleanup();
    } catch (error) {
      console.error(`Failed to stop ${platform} recording:`, error);
      updateCurrentLiveRecordingStatus("error");
      // Don't clear the recovery manifest - keep it intact so the user can retry.
      await cleanup();
      await params.onError(
        error && typeof error === "object"
          ? (error as StopRecordingError)
          : buildStopRecordingError(),
      );
    }
  };

  const pauseRecording = async () => {
    // "warning" is an active-capture state (mic silenced) — pausing is allowed.
    if (status !== "recording" && status !== "warning") {
      return;
    }

    try {
      await plugin.pauseStream();
    } catch (error) {
      console.error(`Failed to pause ${platform} recording:`, error);
      updateCurrentLiveRecordingStatus("error");
    }
  };

  const resumeRecording = async () => {
    if (status !== "paused") {
      return;
    }

    try {
      await plugin.resumeStream();
    } catch (error) {
      console.error(`Failed to resume ${platform} recording:`, error);
      updateCurrentLiveRecordingStatus("error");
    }
  };

  const cancelRecording = async (
    callback: (result: { success: boolean; error?: unknown }) => void,
  ) => {
    const IDLE_STATUSES: LiveRecordingStatus[] = [
      "inactive",
      "stopped",
      "cancelled",
      "permissionDenied",
    ];

    // Truly idle — nothing to cancel.
    if (IDLE_STATUSES.includes(status)) {
      callback({ success: true });
      return;
    }

    if (status === "starting" || startPromiseRef.current) {
      cancelPendingStartRef.current = true;
      const itemId = getRecordingUiState().liveRecording?.itemId;

      try {
        await plugin.cancelStream();
      } catch {
        // If startup is still blocked on permissions, the plugin may not have an
        // active engine yet. The cancelPendingStartRef guard above ensures the
        // just-started engine is cancelled immediately once startup completes.
      }

      if (itemId) {
        await clearLiveRecordingRecoveryManifest(itemId);
      }

      await cleanup();
      callback({ success: true });
      return;
    }

    // "uploading" is only cancellable on platforms that opted in (iOS).
    // On Android, the native upload continues even after cancelStream, so
    // we must report failure so the caller doesn't delete the transcript
    // out from under an active upload.
    if (status === "uploading" && !canCancelWhileUploading) {
      callback({
        success: false,
        error: new Error("Cannot cancel while an upload is in progress."),
      });
      return;
    }

    // recording / paused / error / warning / networkError — attempt cancel.
    try {
      await plugin.cancelStream();
      const itemId = getRecordingUiState().liveRecording?.itemId;
      if (itemId) {
        await clearLiveRecordingRecoveryManifest(itemId);
      }
      await cleanup();
      callback({ success: true });
    } catch (error) {
      console.error(`Failed to cancel ${platform} recording:`, error);
      // The native recorder may still be running. Set status to error but
      // do NOT call cleanup() — that would remove event listeners and leave
      // the user with a dead UI on retry. The modal stays open so the user
      // can try again or force-close the app (which triggers recovery).
      updateCurrentLiveRecordingStatus("error");
      callback({ success: false, error });
    }
  };

  const getRecoveryStatus =
    async (excludeTranscriptIds: readonly string[] = []): Promise<RecordingRecoveryStatus | null> => {
      const manifest = await getLiveRecordingRecoveryForPlatform(platform, excludeTranscriptIds);
      try {
        if (plugin.getEngineState && (await plugin.getEngineState()).isEngineRunning) return null;
        const nativeRecoveryStatus = await plugin.getRecoveryStatus();
        if (nativeRecoveryStatus?.transcriptId && !excludeTranscriptIds.includes(nativeRecoveryStatus.transcriptId)) {
          const nativeManifest = await getLiveRecordingRecoveryForTranscript(nativeRecoveryStatus.transcriptId);
          const startedAt =
            nativeRecoveryStatus.startedAt ?? nativeManifest?.startedAt ?? Date.now();

          await setLiveRecordingRecoveryManifest({
            platform,
            transcriptId: nativeRecoveryStatus.transcriptId,
            status:
              nativeRecoveryStatus.recoveryMode === "blocked"
                ? "blocked"
                : "uploading",
            startedAt,
            uploadedBytes: nativeRecoveryStatus.uploadedBytes,
            finalSequenceNumber: nativeRecoveryStatus.finalSequenceNumber ?? nativeManifest?.finalSequenceNumber,
            lastErrorMessage: nativeRecoveryStatus.lastErrorMessage ?? null,
          });

          return {
            transcriptId: nativeRecoveryStatus.transcriptId,
            finalSequenceNumber: nativeRecoveryStatus.finalSequenceNumber ?? nativeManifest?.finalSequenceNumber,
            startedAt,
            pendingChunks: nativeRecoveryStatus.pendingChunks,
            pendingBytes: nativeRecoveryStatus.pendingBytes,
            uploadedBytes: nativeRecoveryStatus.uploadedBytes,
            recoveryMode: nativeRecoveryStatus.recoveryMode,
            lastErrorMessage: nativeRecoveryStatus.lastErrorMessage ?? null,
          };
        }
      } catch (error) {
        console.error(`Failed to inspect ${platform} recovery status:`, error);
        return null;
      }

      // A missing native backup is not a failed upload: forget only this device's
      // stale record. Server recovery owns any audio already uploaded.
      if (manifest) {
        await clearLiveRecordingRecoveryManifest(manifest.transcriptId);
        return getRecoveryStatus([...excludeTranscriptIds, manifest.transcriptId]);
      }
      return null;
    };

  const resumeRecoveryUpload = async (params: {
    transcriptId: string;
    retryBlocked?: boolean;
  }) => {
    const authToken = getRecordingAuthToken();
    const serverUrl = _getServerUrl();

    if (!authToken || !serverUrl) {
      throw new Error(
        "Missing auth token or server URL for recording recovery.",
      );
    }

    const nativeStatus = await plugin.getRecoveryStatus();
    if (nativeStatus.transcriptId !== params.transcriptId) throw new Error("Recording backup is missing.");
    await addListeners();

    try {
      await completePendingUpload({
        command: () =>
          plugin.resumeRecoveryUpload({
            transcriptId: params.transcriptId,
            retryBlocked: params.retryBlocked,
            authToken,
            serverUrl,
          }),
      });
      // Recovery upload finished but the recovery manifest is intentionally kept
      // here. The caller (recoverInterruptedLiveRecording's onRecovered) performs
      // the server finalize and is responsible for calling discardRecovery if it
      // succeeds. If finalize fails the manifest stays so the user can retry.
      await cleanup();
    } catch (error) {
      console.error(`Failed to resume ${platform} recovery upload:`, error);
      await cleanup();
      throw error;
    }
  };

  const discardRecovery = async (params: { transcriptId: string }) => {
    try {
      await plugin.discardRecovery({ transcriptId: params.transcriptId });
    } catch (error) {
      console.error(`Failed to discard ${platform} recovery backup:`, error);
    }

    await clearLiveRecordingRecoveryManifest(params.transcriptId);
  };

  return {
    status,
    elapsedTime,
    audioLevel,
    recordingHealth,
    pauseOrPlay: status === "paused" ? resumeRecording : pauseRecording,
    startRecording,
    stopRecording,
    cancelRecording,
    getRecoveryStatus,
    resumeRecoveryUpload,
    discardRecovery,
  };
};

type UseLiveRecorder = () => LiveRecorderHook;

let _useLiveRecorder: UseLiveRecorder | undefined;

export const configureLiveRecorder = (useRecorder: UseLiveRecorder): void => {
  _useLiveRecorder = useRecorder;
};

export const useLiveRecorder = (): LiveRecorderHook => {
  if (!_useLiveRecorder) {
    throw new Error("Live recorder used before controller construction");
  }
  return _useLiveRecorder();
};
